/**
 * Lightweight client-side A/B testing utility.
 * Assigns visitors to variants persistently via localStorage.
 * Tracks variant exposure and conversions via GA4.
 */

const STORAGE_KEY = 'pb_ab_tests';

export type ABVariant = 'A' | 'B';

interface ABAssignments {
  [testName: string]: ABVariant;
}

function getAssignments(): ABAssignments {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveAssignments(assignments: ABAssignments): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(assignments));
  } catch {
    // localStorage may be unavailable (private browsing, etc.)
  }
}

/**
 * Returns the A/B variant for a given test name.
 * Assigns randomly (50/50) on first visit, persists for subsequent visits.
 */
export function getABVariant(testName: string): ABVariant {
  const assignments = getAssignments();
  if (assignments[testName]) {
    return assignments[testName];
  }
  const variant: ABVariant = Math.random() < 0.5 ? 'A' : 'B';
  assignments[testName] = variant;
  saveAssignments(assignments);
  trackABExposure(testName, variant);
  return variant;
}

/**
 * Fires a GA4 event when a user is first assigned to a variant.
 */
function trackABExposure(testName: string, variant: ABVariant): void {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', 'ab_test_exposure', {
      test_name: testName,
      variant,
    });
  }
}

/**
 * Fires a GA4 event for A/B test interactions (e.g. CTA clicks).
 */
export function trackABConversion(testName: string, action: string): void {
  const assignments = getAssignments();
  const variant = assignments[testName];
  if (!variant) return;
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', 'ab_test_conversion', {
      test_name: testName,
      variant,
      action,
    });
  }
}

/** Active A/B test identifiers */
export const AB_TESTS = {
  /** CTA button copy on affiliate buttons (all pages via getCtaLabel) */
  CTA_COPY: 'cta_copy_v1',
  /** Homepage CTA copy and placement */
  HOMEPAGE_CTA: 'homepage_cta_v1',
} as const;
